<template>
    <div class="modal-content">
        <div class="modal-contentWrap">
            <div class="modal-header bg-1">
                <modal-close />

                <div class="modal-title mb-0 text-center">
                    <h2 v-text="merchant.name" class="h3 mb-0" />
                    <p v-text="promotion.nam" class="mb-0" />
                </div>
            </div>
            <div class="modal-body">
                <alt-ean-disclaimer
                    v-if="$store.getters.isWeight(promotion.type)"
                    class="mb-3"
                />

                <div
                    v-if="!hasProductSets"
                    class="body-header d-flex mb-3 gap-1"
                >
                    <div class="body-header-searcher form-group mb-0">
                        <search-input
                            class="mb-0 typo--md"
                            :search-input="searchEanOrName"
                            :search-params-tooltip="['nazwie produktu', 'kodzie EAN']"
                            @change="searchEanOrName = $event"
                        />
                    </div>

                    <div
                        v-if="userCanEdit"
                        class="body-header-options"
                    >
                        <money-input
                            class="mb-0"
                            style="max-width: 9rem"
                            :price="massPrice"
                            :label="`Cena ${hasProductSets ? 'zestawu' : 'w promocji'}`"
                            :disabled="false"
                            @onInput="massPrice = $event"
                        />

                        <the-btn
                            :disabled="!massPrice"
                            text="Zastosuj dla wszystkich"
                            @click="applyMassPrice"
                        />
                    </div>
                </div>

                <!-- Products table -->
                <div class="c-table shop-prices">
                    <div class="c-thead gap-1">
                        <div class="c-td">
                            {{ hasProductSets ? 'Zestaw' : 'Produkt' }}
                        </div>
                        <div v-if="isWeight" class="c-td">Kod lokalny</div>
                        <div v-if="promoType === PROMO_TYPES.QUOTA" class="c-td">
                            Rabat brutto
                        </div>
                        <div
                            v-else-if="promoType === PROMO_TYPES.BONDED_QUOTA"
                            class="c-td"
                        >
                            Rabat brutto
                            <span class="nowrap">do zestawu</span>
                        </div>
                        <div
                            v-else-if="promoType === PROMO_TYPES.BONDED_PERCENTAGE"
                            class="c-td"
                        >
                            Rabat %
                            <span class="nowrap">do zestawu</span>
                        </div>
                        <div v-else class="c-td">
                            {{ hasProductSets ? 'Cena zestawu' : 'Cena w promocji' }}
                        </div>
                    </div>

                    <!-- Table body -->
                    <div class="c-tbody">
                        <product-tree
                            v-if="productsPaginated.length"
                            :component-name="hasProductSets
                                ? 'ChangeShopPricesSet'
                                : 'ChangeShopPricesProduct'"
                            :promotion="promotion"
                            :subscription="subscription"
                            :products="productsPaginated"
                            :apply-mass-price="applyMassPrice"
                            :merchant-id="merchant.id"
                        />
                        <div v-else class="c-tr">
                            <div class="c-td">Brak wyników</div>
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="modal-footer bg-1 pt-3 d-flex justify-content-between"
                style="position: sticky; bottom: 0"
            >
                <the-pagination
                    :pagination="pagination"
                    @change="setPagination"
                />

                <v-popover
                    v-if="userCanEdit && noLocalCodes.length"
                    offset="8"
                    class="mt-2 ml-lg-auto ml-0"
                    :container="false"
                >
                    <the-btn
                        :disabled="errors.length > 0 || !isChanged.length || loader"
                    >
                        Zapisz
                        <button-spinner
                            v-if="loader"
                            class="ml-3"
                            status="loading"
                        />
                    </the-btn>

                    <template slot="popover">
                        <popover-confirm
                            :message="noLocalCodesPopoverText"
                            abort-text="Pomiń i modyfikuj"
                            confirm-text="Uzupełnij kody"
                            @aborted="updateSubscription"
                        />
                    </template>
                </v-popover>
                <the-btn
                    v-else-if="userCanEdit"
                    :disabled="errors.length > 0 || !isChanged.length || loader"
                    @click.native="updateSubscription"
                >
                    Zapisz

                    <button-spinner
                        v-if="loader"
                        class="ml-3"
                        status="loading"
                    />
                </the-btn>

                <div
                    v-if="updateError"
                    class="w-100 text-sm text-danger text-right"
                >
                    Zmiana cen nie powiodła się.
                    Napisz na <a href="mailto:bok@europlatform.pl">bok@europlatform.pl</a>
                    lub zadzwoń na numer <a href="tel:48221291400">+48 22 12 91 400</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import promoBonded from '@/mixins/promoBonded'
import AltEanDisclaimer from '@/components/promotions/altEans/AltEanDisclaimer'
import { PROMO_TYPES } from '@/utils/constants/promo-types'

export default {
    mixins: [promoBonded],

    components: {
        AltEanDisclaimer
    },

    data: function() {
        return {
            pagination: {
                nextPage: '',
                page: 1,
                perPage: 10,
                prevPage: '',
                totalPages: '',
                totalRecords: ''
            },
            initialState: null,
            searchEanOrName: '',
            massPrice: null,
            queueTimer: null,
            loader: false,
            noLocalCodes: [],
            updateError: false,
            PROMO_TYPES
        }
    },
    computed: {
        modalArgs() {
            return this.$store.getters.modalState.args || {}
        },
        subscription() {
            return this.modalArgs.subscription || {}
        },

        merchant() {
            return this.$store.getters.getMerchantById(
                this.subscription.merchant_id
            )
        },
        promotion() {
            return this.modalArgs.promotion
        },
        promoType() {
            return this.promotion.type
        },
        group() {
            return this.modalArgs.group
        },
        productSubscriptions() {
            return this.subscription.product_subscriptions || []
        },
        products() {
            const search = this.searchEanOrName.toLowerCase()

            return this.productSubscriptions
                .map(product => {
                    const promoDict = product.promo_dict_product || {}
                    const dict = promoDict.dict_product || {}
                    return {
                        id: product.promo_dict_product_id,
                        productId: product.id,
                        price: product.price,
                        available_in_promos: dict.available_in_promos,
                        name: dict.name,
                        ean: dict.ean,
                        alternative_ean: product.alternative_ean || '',
                        discount_amount: promoDict.discount_amount,
                        max_price: dict.maximum_price,
                        promo_dict_product_id: product.promo_dict_product_id,
                        eanErrors: product.eanErrors,
                        eanPresent: promoDict.ean_present,
                        main: dict.main,
                        promo_dict_product_eans_count: promoDict.promo_dict_product_eans_count
                    }
                })
                .filter(product => {
                    const name = product.name.toString().toLowerCase()
                    const ean = product.ean.toString().toLowerCase()
                    const localEan = product.alternative_ean.toString().toLowerCase()

                    return (
                        name.includes(search) ||
                        ean.includes(search) ||
                        localEan.includes(search)
                    )
                })
        },
        productSets() {
            const promoSets = this.$store.getters.getPromoProductSets
            const subscriptionSets = this.subscription.product_set_subscriptions || []
            const setNname = `Zestaw produktów ${this.isBonded ? 'rabatowanych' : 'promocyjnych'}`
            const promoType = this.promoType

            const sets = subscriptionSets.filter(set => {
                // Filter out trigger sets
                const promoSet = promoSets.find(({ id }) => id === set.product_set_id)
                if (!promoSet) return false

                if (promoSet.type === 'ProductSet::Trigger') {
                    // If the promo set is a trigger, we don't want to include it
                    return false
                }

                return true
            }).map((set, idx, sets) => {
                const promoSet = promoSets.find(({ id }) => id === set.product_set_id)

                let price
                if (promoType === PROMO_TYPES.BONDED_QUOTA && promoSet) {
                    price = -(promoSet.discount_amount * +promoSet.required_weight)
                } else if (promoType === PROMO_TYPES.BONDED_PERCENTAGE && promoSet) {
                    price = -promoSet.discount_percentage
                } else {
                    price = set.price
                }

                let name = setNname
                if (sets.length > 1) {
                    name += ' ' + (idx + 1)
                }

                return {
                    id: set.id,
                    price: price || 0,
                    name,
                    ean: '',
                    max_price: (promoSet || {}).price,
                    main: true
                }
            }
            )
            return sets
        },
        productsPaginated() {
            if (!this.products.length) {
                // If there are no products, return the product sets
                return this.productSets
            }

            // Otherwise, return the paginated products
            const { page, perPage } = this.pagination

            return (this.products || []).filter((_, index) => {
                const start = (page - 1) * perPage
                const end = start + perPage
                return index >= start && index < end
            })
        },

        hasProductSets() {
            return this.$store.getters.hasProductSets(this.promoType)
        },

        isChanged() {
            const { initialState, subscription } = this
            if (!initialState) return false

            const productsKey = this.hasProductSets
                ? 'product_set_subscriptions'
                : 'product_subscriptions'

            return (subscription[productsKey] || []).filter(prodNew => {
                const prodInit = initialState[productsKey].find(
                    prod => prod.id === prodNew.id
                )

                const eanChanged =
                    (prodNew.alternative_ean || prodInit.alternative_ean) &&
                    prodNew.alternative_ean !== prodInit.alternative_ean

                const priceChanged =
                    Number(prodNew.price) !== Number(prodInit.price)

                // Return true if either EAN or price has changed
                return eanChanged || priceChanged
            })
        },

        isPromoEditPermission() {
            return this.$store.getters.isPromoEditPermission
        },

        isEditablePromo() {
            return this.$store.getters.isEditablePromo(this.promotion.type)
        },

        userCanEdit() {
            return this.isEditablePromo && this.isPromoEditPermission
        },

        isWeight() {
            return !this.promotion.all_eans_present && !this.hasProductSets
        },

        noLocalCodesPopoverText() {
            return this.$store.getters.isWeight(this.promoType)
                ? 'Modyfikujesz promocję wagową. Upewnij się, że kody lokalne towarów wagowych zostały poprawnie przypisane, w przeciwnym razie promocja może nie działać poprawnie.'
                : 'Promocja zawiera kody lokalne. Upewnij się, że zostały one poprawnie przypisane. W przeciwnym razie promocja może nie działać prawidłowo.'
        },

        errors() {
            return this.$store.getters.getSubscriptionLocalCodeErrors
        }
    },
    methods: {
        applyMassPrice() {
            const self = this
            this.subscription.product_subscriptions.forEach(product => {
                self.$set(product, 'price', (+this.massPrice).toFixed(2))
            })
            // Force reactivity on modalArgs.subscription so productsPaginated updates
            this.$set(this.modalArgs, 'subscription', { ...this.subscription })
            this.validate()
        },

        validate() {
            const products = this.products.length
                ? this.products
                : this.productSets

            const errors = []
            const noLocalCodes = []

            // Check only main products
            const mainProducts = products.filter(product => product.main)

            mainProducts.forEach(product => {
                const price = +product.price
                const maxPrice = +product.max_price

                if (price === 0 || price > maxPrice) {
                    // Check if price is zero or greater than the max price, and log an error if so
                    errors.push({ id: product.id, type: 'price' })
                }

                if (!this.isWeight) {
                    // If it's not a weight promo, skip further checks
                    return
                }

                // Check if there's any duplicated alternative ean
                const isDuplicateAlternativeEan =
                    mainProducts.filter(
                        p =>
                            p.alternative_ean === product.alternative_ean &&
                            p.alternative_ean
                    ).length > 1

                if (isDuplicateAlternativeEan) {
                    errors.push({ id: product.id, type: 'code' })
                }

                if (!product.alternative_ean) {
                    // If the product has no alternative EAN, log an error for missing code
                    noLocalCodes.push({ id: product.id, type: 'nocode' })
                }
            })

            this.noLocalCodes = noLocalCodes
            this.$store.dispatch('setSubscriptionLocalCodeErrors', errors)
        },

        setPagination(page) {
            const { pagination } = this

            const products = this.products.length
                ? this.products.filter(p => p.main)
                : this.productSets

            const totalRecords = products.length
            const totalPages = Math.ceil(products.length / pagination.perPage)
            const nextPage = page < totalPages ? page + 1 : null
            const prevPage = page > 1 ? page - 1 : null

            pagination.page = page || 1
            pagination.totalRecords = totalRecords
            pagination.totalPages = totalPages || 1
            pagination.nextPage = nextPage
            pagination.prevPage = prevPage
        },

        resetInitState() {
            this.initialState = JSON.parse(JSON.stringify(this.subscription))
        },

        async updateSubscription() {
            this.updateError = false
            try {
                if (this.errors.length > 0 || !this.isChanged || this.loader) {
                    return
                }

                const altEansToSave = []
                const altEansToUpdate = []

                const pushAltEans = (errors, product) => {
                    const merchantId = this.merchant.id
                    const altEan = product.alternative_ean
                    const dictProdId = product.promo_dict_product.dict_product_id

                    if (altEan && errors?.length) {
                        const err = errors.find(er => {
                            return (
                                er.alternative_ean === altEan &&
                                er.merchant_id === merchantId
                            )
                        })
                        if (err) {
                            altEansToUpdate.push({
                                id: err.id,
                                alternative_ean: altEan
                            })
                        } else {
                            altEansToSave.push({
                                merchant_id: merchantId,
                                alternative_ean: altEan,
                                dict_product_id: dictProdId
                            })
                        }
                    } else if (altEan) {
                        altEansToSave.push({
                            merchant_id: merchantId,
                            alternative_ean: altEan,
                            dict_product_id: dictProdId
                        })
                    }
                }

                const products = (this.subscription.product_subscriptions || []).map(
                    p => {
                        const { price, alternative_ean, id } = p

                        pushAltEans(p.eanErrors, p)

                        return {
                            id,
                            price: parseFloat(price),
                            alternative_ean: alternative_ean || ''
                        }
                    }
                )

                const product_sets = (this.subscription.product_set_subscriptions || []).filter((set) => {
                    return !!set.price
                }).map(p => {
                    const { price, id } = p

                    return {
                        id,
                        price: parseFloat(price)
                    }
                })

                const subscription = {
                    id: this.subscription.id,
                    merchant_id: this.merchant.id,
                    product_sets,
                    products,
                    promo_id: this.subscription.promo_id,
                    state: this.subscription.state
                }

                const groups = [
                    {
                        acceptance: this.group.acceptance,
                        id: this.group.id,
                        merchant_ids: [this.merchant.id],
                        subscriptions: [subscription]
                    }
                ]

                const payload = {
                    userId: this.$store.getters.userId,
                    state: 'update',
                    groups
                }

                if (altEansToSave.length) {
                    this.$store.$api.post('saveMerchantAlternativeEans', {
                        save: altEansToSave
                    })
                }

                this.loader = true
                const patch = await this.$store.$api.patch(
                    'subscriptionUpdate',
                    payload
                )

                const response = patch.data.data
                const queueId = response.queue_id

                this.$store.commit('SET_SUBSCRIPTIONS_QUEUEID', queueId)

                const checkProgress = async() => {
                    const res = await this.$store.$api.get(
                        `subscriptionQueueProgress/${queueId}`
                    )

                    return res.data.data
                }

                setTimeout(() => {
                    startInterval()
                }, 1000)

                const startInterval = () => {
                    this.queueTimer = setInterval(async() => {
                        const { status, processed, total } = await checkProgress()

                        if (status === 'waiting' || status === 'processing') {
                            // Do nothing, data is still processing
                            return
                        }

                        // Not processing anymore; clear the timer
                        this.clearTimer()

                        if (status === 'error' || processed !== total) {
                            // Show error notification
                            this.updateError = true

                            this.loader = false
                            return
                        }

                        // All good, accepted and error subscriptions
                        const accepted = await this.$store.dispatch(
                            'fetchProcessedSubscriptions',
                            {
                                queueId,
                                type: 'successed',
                                status: 'accepted'
                            }
                        )

                        if (accepted.data.data.accepted.count > 0) {
                            this.$store.dispatch('openModal', {
                                component: 'ModalEditPromoSummary'
                            })

                            this.loader = false
                            return
                        }

                        const errors = await this.$store.dispatch(
                            'fetchProcessedSubscriptions',
                            {
                                type: 'error',
                                status: 'error'
                            }
                        )

                        this.loader = false

                        if (!errors.data?.data?.error?.count) {
                            throw new Error(errors?.data?.message || 'Error fetchProcessedSubscriptions')
                        }

                        let errorText = 'Wystąpił błąd. Spróbuj ponownie później.'
                        const errorPromos = errors.data.data.error.promos || []
                        if (errorPromos.length > 0) {
                            const firstPromoError = errorPromos[0].description

                            if (firstPromoError.trim() !== '') {
                                errorText = firstPromoError
                            }
                        }

                        this.$store.dispatch('addNotification', {
                            type: 'danger',
                            content: errorText
                        })

                        this.updateError = true
                    }, 1000)
                }
            } catch (error) {
                this.loader = false
                this.$store.dispatch('addNotification', {
                    type: 'danger',
                    content: 'Wystąpił błąd. Spróbuj ponownie później.'
                })
                console.log(error)
            }
        },

        clearTimer() {
            clearInterval(this.queueTimer)
            this.queueTimer = null
        }
    },

    beforeDestroy() {
        this.clearTimer()

        this.subscription.product_subscriptions = this.initialState.product_subscriptions
        this.subscription.product_set_subscriptions = this.initialState.product_set_subscriptions
    },

    created() {
        this.setPagination(1)

        this.resetInitState()
    },

    watch: {
        searchEanOrName() {
            this.setPagination(1)
        },
        'subscription.product_set_subscriptions': {
            immediate: true,
            deep: true,
            handler() {
                this.validate()
            }
        },
        'subscription.product_subscriptions': {
            immediate: true,
            deep: true,
            handler() {
                this.validate()
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import 'node_modules/bootstrap/scss/functions';
@import 'node_modules/bootstrap/scss/variables';
@import 'node_modules/bootstrap/scss/mixins';

.body-header {
    @include media-breakpoint-down(md) {
        flex-direction: column;
    }
}
.body-header-searcher {
    min-width: 300px;
}

.body-header-options {
    display: flex;
    gap: 1rem;
    width: 100%;
    justify-content: flex-end;
    align-items: start;
}
</style>
